import {useEffect, useRef} from 'react'
import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions-types/agent-quota-snapshot'
import type {ChatState} from '@microsoft/agent-host-protocol'
import type {Session} from '../types/session'
import {isPendingSession, isActiveSession} from '../types/session'
import {useAgentQuotaSnapshot} from './use-agent-quota-snapshot'

/**
 * Returns the freshest available quota snapshot, refreshing automatically
 * when a turn settles or the active session transitions from running to stopped.
 */
export function useFreshQuotaSnapshot(
  initial: AgentQuotaSnapshot | undefined,
  activeSession: Session | null,
  ahpChatState?: ChatState,
): AgentQuotaSnapshot | undefined {
  const {data: refreshedAgentQuotaSnapshot, refetch: refetchAgentQuotaSnapshot} = useAgentQuotaSnapshot({
    enabled: false,
  })

  const previousRef = useRef<{
    sessionId?: string
    running: boolean
    activeTurnId?: string
    refreshedAfterTurn: boolean
  }>({running: false, refreshedAfterTurn: false})
  const sessionId = activeSession?.id
  const isSessionRunning = !!activeSession && (isPendingSession(activeSession) || isActiveSession(activeSession))
  const activeTurnId = ahpChatState?.activeTurn?.id
  const hasAhpState = ahpChatState !== undefined

  useEffect(() => {
    const previous = previousRef.current
    const sameSession = !!sessionId && previous.sessionId === sessionId
    const turnSettled = sameSession && previous.activeTurnId !== undefined && hasAhpState && activeTurnId === undefined
    const sessionStopped = previous.running && !isSessionRunning && !previous.refreshedAfterTurn
    if (turnSettled || sessionStopped) {
      void refetchAgentQuotaSnapshot()
    }
    previousRef.current = {
      sessionId,
      running: isSessionRunning,
      activeTurnId: hasAhpState ? activeTurnId : sameSession ? previous.activeTurnId : undefined,
      refreshedAfterTurn: turnSettled || (sameSession && activeTurnId === undefined && previous.refreshedAfterTurn),
    }
  }, [activeTurnId, hasAhpState, isSessionRunning, refetchAgentQuotaSnapshot, sessionId])

  return refreshedAgentQuotaSnapshot ?? initial
}
